// NOTE: A smart link's matched_text is a *clause* the run cited, drawn from a tag-stripped,
// whitespace-normalised rendering of the body (see buildEvidenceSnippet in
// core/models/smart-link-snippet.ts) — never a span, and frequently not containing the fund's
// name at all. So locating a mention is two lookups against that same normalisation: the clause
// in the body, then the name inside that clause. buildEvidenceSnippet cannot be reused because
// its index is into a detached plaintext string with no route back to the DOM; this module keeps
// the route, which is the whole point of it.

// NOTE: TinyMCE parks caret containers and editor-only chrome — the body block's remove cross
// among them — in data-mce-bogus subtrees, and none of it survives serialisation. Their text is
// therefore absent from the run content, so walking it would both shift every index past it and
// let an anchor be wrapped around chrome. Script and style content is skipped on the same
// principle: it is not prose, and must never become a link.
const SKIPPED_ELEMENTS: ReadonlySet<string> = new Set(['SCRIPT', 'STYLE']);
const BOGUS_ATTRIBUTE = 'data-mce-bogus';

// NOTE: Tag names rather than computed style: the walk has to agree with itself in a unit test's
// iframe, where content_css has not necessarily loaded, and a display value cannot be read off a
// detached node at all.
const BLOCK_ELEMENTS: ReadonlySet<string> = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BODY',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'UL',
]);

// NOTE: Emitted as nothing rather than as whitespace. A zero-width space is TinyMCE's inline
// boundary marker and sits *inside* words, so collapsing it to a space would split one.
const ZERO_WIDTH = /[\uFEFF\u200B]/;

// Unicode-aware so an accented fund name ("Amundi Société Générale") is delimited by the same
// rule as an ASCII one, rather than every accent reading as a word boundary.
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

interface IIndexedCharacter {
  node: Text;
  offset: number;
  block: Element;
}

export interface IBodyTextIndex {
  // Whitespace-normalised body text: the string the clause and the name are matched against.
  text: string;
  // Parallel to `text` — characters[i] is where text[i] came from. Same length, always.
  characters: IIndexedCharacter[];
}

function isSkipped(element: Element): boolean {
  return (
    SKIPPED_ELEMENTS.has(element.tagName) ||
    element.hasAttribute(BOGUS_ATTRIBUTE)
  );
}

function nearestBlock(node: Node, root: Element): Element {
  let current = node.parentElement;
  while (current && current !== root) {
    if (BLOCK_ELEMENTS.has(current.tagName)) {
      return current;
    }
    current = current.parentElement;
  }
  return root;
}

function isWithinSkippedElement(node: Node, root: Element): boolean {
  let current = node.parentElement;
  while (current && current !== root) {
    if (isSkipped(current)) {
      return true;
    }
    current = current.parentElement;
  }
  return isSkipped(root);
}

// NOTE: Normalises exactly as smart-link-snippet's normalise() does — \s+ to one space, trimmed —
// because the needle it is matched against went through that function. Divergence here surfaces
// as an unexplainable miss, so the two have to be read together.
export function buildBodyTextIndex(root: Element): IBodyTextIndex {
  const characters: IIndexedCharacter[] = [];
  const parts: string[] = [];
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT
  );
  let previousBlock: Element | null = null;

  for (
    let textNode = walker.nextNode() as Text;
    textNode;
    textNode = walker.nextNode() as Text
  ) {
    if (isWithinSkippedElement(textNode, root)) {
      continue;
    }
    const block = nearestBlock(textNode, root);
    // A block boundary reads as a space, since the tag it replaces became one when the run
    // content was flattened. Without it "…inflows</p><p>StepStone…" joins into a single word and
    // can match a clause that does not appear in the prose at all.
    if (
      previousBlock !== null &&
      block !== previousBlock &&
      parts.length > 0 &&
      parts[parts.length - 1] !== ' '
    ) {
      parts.push(' ');
      characters.push({ node: textNode, offset: 0, block });
    }
    previousBlock = block;

    const data = textNode.data;
    for (let offset = 0; offset < data.length; offset++) {
      const character = data[offset];
      if (ZERO_WIDTH.test(character)) {
        continue;
      }
      if (/\s/.test(character)) {
        // Leading whitespace is dropped and runs collapse, so the index stays aligned with a
        // string that has been through normalise().
        if (parts.length === 0 || parts[parts.length - 1] === ' ') {
          continue;
        }
        parts.push(' ');
        characters.push({ node: textNode, offset, block });
        continue;
      }
      parts.push(character);
      characters.push({ node: textNode, offset, block });
    }
  }

  while (parts.length > 0 && parts[parts.length - 1] === ' ') {
    parts.pop();
    characters.pop();
  }

  return { text: parts.join(''), characters };
}

const normalise = (value: string): string =>
  (value || '').replace(/\s+/g, ' ').trim();

function isWordBounded(text: string, start: number, end: number): boolean {
  const before = start > 0 ? text[start - 1] : '';
  const after = end < text.length ? text[end] : '';
  return !WORD_CHARACTER.test(before) && !WORD_CHARACTER.test(after);
}

// NOTE: Rejects a candidate that overlaps an anchor *at all*, not merely one contained in one.
// Partial overlap is how "<a>Blackstone Real Estate</a> Income Trust" gets emitted — a
// shorter-named fund having already claimed a prefix of a longer one — which points the link at
// the wrong fund and blocks the right one. Testing the start node alone would miss it, so every
// character of the candidate is tested.
function overlapsAnchor(
  index: IBodyTextIndex,
  start: number,
  end: number,
  root: Element
): boolean {
  for (let at = start; at < end; at++) {
    let current = index.characters[at].node.parentElement;
    while (current && current !== root) {
      if (current.tagName === 'A') {
        return true;
      }
      current = current.parentElement;
    }
  }
  return false;
}

function toRange(
  index: IBodyTextIndex,
  start: number,
  end: number,
  root: Element
): Range {
  const first = index.characters[start];
  const last = index.characters[end - 1];
  const range = root.ownerDocument.createRange();
  range.setStart(first.node, first.offset);
  // end-1 is the match's last character, and a normalised needle never ends in whitespace, so
  // that character is real prose and its offset+1 closes the range on it.
  range.setEnd(last.node, last.offset + 1);
  return range;
}

/**
 * Locates a fund mention to link: finds the run's evidence clause in the body, then the fund's
 * name inside that clause, and returns the name's span as a Range.
 *
 * Matching is exact — whitespace-normalised and word-boundary delimited. The clause matches
 * case-insensitively; the name only in the dataset's own casing (ADR-0008). No fuzzy matching and
 * no progressive truncation, which would degrade "Blackstone Real Estate Income Trust" to
 * "Blackstone" and publish a different fund's NAV under this fund's name (ADR-0006).
 *
 * Returns null when the clause is absent, when the name is absent from it, or when every
 * occurrence is disqualified — by overlapping an anchor, or by straddling a block boundary, which
 * would mean wrapping a link across a paragraph end.
 */
export function findMentionRange(
  root: Element,
  clause: string,
  name: string
): Range | null {
  const needle = normalise(clause);
  const target = normalise(name);
  if (!needle || !target) {
    return null;
  }

  const index = buildBodyTextIndex(root);
  // TODO BUG: clauseAt and clauseEnd come off the lowercased copy but index into index.text,
  // so a code point whose lowercase is longer (U+0130) ahead of the clause desyncs the span.
  const haystack = index.text.toLowerCase();
  const clauseAt = haystack.indexOf(needle.toLowerCase());
  if (clauseAt === -1) {
    return null;
  }
  const clauseEnd = clauseAt + needle.length;

  // Only inside the cited clause. An occurrence of the name the run did not cite is left alone,
  // even an earlier one — every link this creates is one the run vouched for (ADR-0006).
  // NOTE: Against index.text, not haystack: the name is the one case-sensitive lookup (ADR-0008).
  let searchFrom = clauseAt;
  while (searchFrom < clauseEnd) {
    const start = index.text.indexOf(target, searchFrom);
    if (start === -1 || start + target.length > clauseEnd) {
      return null;
    }
    const end = start + target.length;
    searchFrom = start + 1;

    if (!isWordBounded(index.text, start, end)) {
      continue;
    }
    if (index.characters[start].block !== index.characters[end - 1].block) {
      continue;
    }
    if (overlapsAnchor(index, start, end, root)) {
      continue;
    }
    return toRange(index, start, end, root);
  }
  return null;
}

// NOTE: The run abridges some clauses with an ellipsis ("Trust, Inc. ... also saw inflows"), so
// a clause is not guaranteed verbatim and can match nothing in a body nobody has touched. Only
// the flash chases the pieces; findMentionRange stays exact, because it places a link (ADR-0007).
const ELLIPSIS = /\s*(?:\.{3,}|…)\s*/;

// NOTE: Below this, every piece of an abridged clause is too generic ("the fund ... it kept") to
// say which paragraph the run was quoting, so the clause is a miss rather than a guess.
const MIN_IDENTIFYING_PIECE_WORDS = 3;

/**
 * Why a set of clauses flashed nothing: gone from the body, or abridged across paragraphs with a
 * piece that repeats, so no block can be chosen.
 */
export type ClauseMissReason = 'absent' | 'split';

interface IClauseLocation {
  starts: number[];
  split: boolean;
}

function occurrencesOf(haystack: string, lowerNeedle: string): number[] {
  const starts: number[] = [];
  // NOTE: Advanced by one rather than by the needle's length, so a clause that overlaps itself
  // is not halved.
  for (
    let at = haystack.indexOf(lowerNeedle);
    at !== -1;
    at = haystack.indexOf(lowerNeedle, at + 1)
  ) {
    starts.push(at);
  }
  return starts;
}

function abridgedPieces(needle: string): string[] {
  const pieces = needle
    .split(ELLIPSIS)
    .map((piece) => piece.trim())
    .filter((piece) => piece);
  const identifies = pieces.some(
    (piece) => piece.split(' ').length >= MIN_IDENTIFYING_PIECE_WORDS
  );
  return identifies ? pieces : [];
}

// NOTE: A block holding every piece is preferred. Failing that, a run can stitch sentences from
// different paragraphs into one quote, so each piece's own block is lit instead — but only when
// every piece occurs exactly once, since a repeated piece cannot say which paragraph the run
// meant, and guessing is the false positive the fallback must not add.
function locateAbridgedClause(
  index: IBodyTextIndex,
  haystack: string,
  pieces: string[]
): IClauseLocation {
  const startsByPiece = pieces.map((piece) =>
    occurrencesOf(haystack, piece.toLowerCase())
  );
  if (startsByPiece.some((pieceStarts) => pieceStarts.length === 0)) {
    return { starts: [], split: false };
  }

  const blocksByPiece = startsByPiece.map(
    (pieceStarts) =>
      new Set(pieceStarts.map((at) => index.characters[at].block))
  );
  const colocated = startsByPiece[0].filter((at) =>
    blocksByPiece.every((blocks) => blocks.has(index.characters[at].block))
  );
  if (colocated.length > 0) {
    return { starts: colocated, split: false };
  }

  const isUnambiguous = startsByPiece.every(
    (pieceStarts) => pieceStarts.length === 1
  );
  if (!isUnambiguous) {
    return { starts: [], split: true };
  }
  return {
    starts: startsByPiece.map(([onlyStart]) => onlyStart),
    split: false,
  };
}

function locateClause(
  index: IBodyTextIndex,
  haystack: string,
  needle: string
): IClauseLocation {
  const starts = occurrencesOf(haystack, needle.toLowerCase());
  if (starts.length > 0) {
    return { starts, split: false };
  }
  const pieces = abridgedPieces(needle);
  const isAbridged = pieces.length > 0 && pieces.join(' ') !== needle;
  if (!isAbridged) {
    return { starts: [], split: false };
  }
  return locateAbridgedClause(index, haystack, pieces);
}

const toNeedles = (clauses: string[]): string[] =>
  (clauses ?? []).map(normalise).filter((clause) => clause);

/**
 * Locates the blocks to flash for a set of evidence clauses: the element containing every
 * occurrence of each clause, deduped, in document order, skipping clauses the body no longer
 * contains. A clause abridged with an ellipsis flashes the block holding all of its pieces or,
 * when they sit in different blocks and each occurs once, the block of each piece.
 *
 * Deliberately looser than findMentionRange: no name lookup, no word-boundary rule, no
 * anchor-overlap rejection, every occurrence rather than the first, and abridged clauses matched
 * by their pieces. Why each is dropped or widened is ADR-0007.
 */
export function findClauseBlocks(root: Element, clauses: string[]): Element[] {
  const needles = toNeedles(clauses);
  if (needles.length === 0) {
    return [];
  }

  // NOTE: One index for the whole set: the walk is the expensive half, which is also why the
  // card memoises its evidence rows.
  const index = buildBodyTextIndex(root);
  const haystack = index.text.toLowerCase();

  // NOTE: Every occurrence, not the first — ADR-0007 carries why.
  const found = new Set<number>();
  needles.forEach((needle) =>
    locateClause(index, haystack, needle).starts.forEach((at) =>
      // NOTE: The first character, not the span: a clause running past the end of its paragraph
      // flashes the block it started in rather than being dropped for straddling one.
      found.add(at)
    )
  );

  const blocks: Element[] = [];
  Array.from(found)
    .sort((left, right) => left - right)
    .forEach((at) => {
      const block = index.characters[at].block;
      if (!blocks.includes(block)) {
        blocks.push(block);
      }
    });
  return blocks;
}

/**
 * Says why findClauseBlocks found nothing for these clauses. 'split' when any clause was abridged
 * and every piece is in the body but no single block holds them all and some piece repeats, since
 * that explains the miss better than an edit would; 'absent' otherwise. Walks the body again, so
 * call it only on a miss.
 */
export function explainClauseMiss(
  root: Element,
  clauses: string[]
): ClauseMissReason {
  const index = buildBodyTextIndex(root);
  const haystack = index.text.toLowerCase();
  const isSplit = toNeedles(clauses).some(
    (needle) => locateClause(index, haystack, needle).split
  );
  return isSplit ? 'split' : 'absent';
}
